import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@/styled-system/css';
import Contact from 'app/shared/modules/hdp/form/Contact/Contact';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers';
import { useContactSubmission } from 'app/shared/hooks/useContactSubmission';

/**
 * Listing Card Contact Modal
 *
 * Renders the contact form modal for listings on the search results page (SRP).
 * Uses listing data from the SRP API and provides fallbacks for missing fields.
 */

interface ListingCardContactModalProps {
  listing: ListingDetails;
  onClose?: () => void;
}

const ListingCardContactModal: React.FC<ListingCardContactModalProps> = ({ listing: rawListing, onClose }) => {
  const dispatch = useDispatch();

  // Memoize the listing object to prevent infinite loop in useEffect
  // The listing object was being recreated on every render, causing the useEffect
  // to continuously dispatch SET_CURRENT_LISTING actions
  const listing = useMemo(() => {
    return {
      ...rawListing,
      details: {
        ...rawListing.details,
        studentHousing: rawListing.studentHousing || false,
        seniorHousing: rawListing.seniorHousing || false,
        militaryHousing: rawListing.militaryHousing || false,
        rental: true,
        trusted: rawListing.trusted || false,
        demoted: false,
      },
    };
  }, [rawListing]);

  const listingCardContactModalStyles = css({
    width: '100%',
    height: 'auto',
    maxHeight: '90vh',
    display: 'block',
    position: 'relative',
    overflowX: 'visible',
    overflowY: 'visible',
    '@media (min-width: 768px)': {
      width: '460px',
      maxWidth: '90vw',
    },
    '&, & *': {
      boxSizing: 'border-box !important',
      maxWidth: '100% !important',
    },
    '& .ContactForm, & .ContactForm-popup': {
      width: '100% !important',
      maxWidth: '100% !important',
      padding: '20px !important',
      margin: '0 !important',
      overflowX: 'hidden !important',
      overflowY: 'hidden !important',
    },
    '& input, & textarea, & select, & .Input, & .InputPhoneNumber': {
      width: '100% !important',
      maxWidth: '100% !important',
      minWidth: '0 !important',
      overflowX: 'hidden !important',
      overflowY: 'hidden !important',
    },
    '& .ContactForm-tip': {
      position: 'relative !important',
      left: 'auto !important',
      right: 'auto !important',
      width: '100% !important',
      marginTop: '8px !important',
      marginRight: '0 !important',
      marginBottom: '8px !important',
      marginLeft: '0 !important',
    },
    '& .ContactForm-tip:before': {
      display: 'none !important',
    },
  });

  const clientLoaded = useSelector((state: RootReduxState) => state.app.clientLoaded);
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const user = useSelector((state: RootReduxState) => state.user);
  const userInquiryDataCache = useSelector((state: RootReduxState) => state.user.inquiryDataCache || {});
  const isLoggedIn = useSelector((state: RootReduxState) => state.user.loggedIn);
  const hasShownPostContactSavedSearch = useSelector(
    (state: RootReduxState) => state.app.hasShownPostContactSavedSearch,
  );

  // Use the contact submission hook - it handles all submission logic
  const { handleSubmitForm, handlePhoneClick, formState, clearErrors } = useContactSubmission({
    listing,
    user,
    dispatch,
    onClose,
    isPopup: true,
    isLoggedIn,
    hasShownPostContactSavedSearch,
    triggerLocation: 'search_results',
    triggerObject: 'property_card',
  });

  // HP-7384: Set previewListing directly (not via setCurrentListing or the
  // setPreviewListing action creator) to keep the map info window visible while the
  // contact modal is open without side effects:
  // - setCurrentListing also updates activeMarkerMaloneLotId → full marker re-render → photo flash
  // - setPreviewListing action creator also fires a "previewed" analytics event
  // Dispatching SET_PREVIEW_LISTING directly avoids both issues.
  useEffect(() => {
    dispatch({ type: 'SET_PREVIEW_LISTING', previewListing: listing });
    return () => {
      dispatch({ type: 'CLEAR_PREVIEW_LISTING' });
    };
  }, [listing, dispatch]);

  const handleSetActiveModal = useCallback((modal: string | null) => {
    if (process.env.NODE_ENV === 'development' && modal) {
      // eslint-disable-next-line no-console
      console.warn(`⚠️ SRP modal attempted to open sub-modal: ${modal} - not supported in SRP context`);
    }
  }, []);

  const renderModal = useCallback(() => {
    return null;
  }, []);

  if (formState.errorMessage) {
    return (
      <div className={listingCardContactModalStyles}>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#dc2626' }}>Error</div>
          <div style={{ color: '#6b7280', marginBottom: '16px' }}>{formState.errorMessage}</div>
          <button
            onClick={clearErrors}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007cba',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className={listingCardContactModalStyles}>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '16px', color: '#6b7280' }}>Unable to load listing information.</div>
          <button
            onClick={onClose}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={listingCardContactModalStyles}>
      {formState.contactSubmitted ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#22c55e' }}>
            Message Sent!
          </div>
          <div style={{ color: '#6b7280' }}>Your inquiry has been sent to the property manager.</div>
        </div>
      ) : (
        <Contact
          listing={listing}
          clickstreamEvent={{
            triggerLocation: 'search_results',
            triggerObject: 'property_card',
          }}
          isPopup={true}
          onSubmitForm={handleSubmitForm}
          onSetActiveModal={handleSetActiveModal}
          onClickPhoneContact={handlePhoneClick}
          emailErrorMessage={formState.emailErrorMessage}
          phoneErrorMessage={formState.phoneErrorMessage}
          onClose={onClose}
          onHidePopup={onClose}
          clientLoaded={clientLoaded}
          isMobile={isMobile}
          user={user}
          userInquiryDataCache={userInquiryDataCache}
          renderModal={renderModal}
          dispatch={dispatch}
        />
      )}
    </div>
  );
};

export default ListingCardContactModal;
